import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { twMerge } from 'tailwind-merge';
import { useIsTruncated } from '@/shared/hooks/useIsTruncated/useIsTruncated';

/** Static so Tailwind can see each class; a template literal would not be scanned. */
/* eslint-disable i18next/no-literal-string -- Tailwind class names, not copy */
const CLAMP_CLASS = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
} as const;
/* eslint-enable i18next/no-literal-string */

interface TruncatedTextProps {
  value: string | null | undefined;
  /** Visible line count before clamping. Defaults to a single truncated line. */
  lines?: keyof typeof CLAMP_CLASS;
  /** Tailwind classes applied to the span itself (font weight, color, etc.). */
  className?: string;
  /** Rendered when value is empty / nullish. Defaults to a muted em-dash. */
  fallback?: React.ReactNode;
}

/**
 * Text that truncates or clamps, with a hover tooltip that only renders when
 * the span actually overflows. Mirrors `ArtifactNameCell.TruncatedLine` so
 * the same overflow-detection UX (ResizeObserver via `useIsTruncated`) is
 * shared across tables. The Tooltip wrapper is conditional on
 * `isTruncated` so short-content rows render as a bare span — no redundant
 * tooltip on hover.
 *
 * Use inside a `flex min-w-0` container so the span can shrink below its
 * intrinsic content width and the truncation actually kicks in.
 *
 * The tooltip panel is capped rather than left at its default `w-fit`, which
 * would render a long value as one viewport-wide line. `break-words` is what
 * makes the cap hold for a value with no spaces to break at, such as a pasted
 * identifier.
 */
export function TruncatedText(props: TruncatedTextProps) {
  const display = props.value ?? '';
  const { setNode, isTruncated } = useIsTruncated();

  if (!display) {
    return <>{props.fallback ?? <span className="text-sm text-muted-foreground">—</span>}</>;
  }

  const span = (
    <span
      ref={setNode}
      // Merged rather than concatenated because a clamp sets `display` itself:
      // concatenated, `block` and `line-clamp-2` both land and `block` wins, so
      // the text never clamps and the overflow check never fires. `truncate`
      // keeps `block`, which it needs.
      className={twMerge('block min-w-0', CLAMP_CLASS[props.lines ?? 1], props.className)}
    >
      {display}
    </span>
  );

  if (!isTruncated) return span;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{span}</TooltipTrigger>
        {/* The value is a full sentence when clamped, so cap the panel rather
            than let it run the width of the viewport. */}
        <TooltipContent className="max-w-sm">{display}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
